import { createRoot } from "react-dom/client";
import { api } from "@/lib/api";
import { lohnZeilen, lohnSumme, lohnBeschreibung, rabattFaktor } from "@/lib/lohnanteil";

/**
 * Rückfrage vor dem Drucken/PDF/Senden (BUT-0030, 29.10.2026): Erscheint der Lohnanteil auf dem Beleg, bestätigt Ralph ihn mit einem Klick.
 * EINE Stelle für alle Wege: Beleg-Editor (Senden, Mail, Drucken, PDF, Vorschau), Listen Angebote/AB/Rechnungen (PDF),
 * Belegvorschau (Drucken, .eml). Das Fenster hängt sich selbst an die Seite – es braucht keinen eigenen Zustand der Seite.
 *
 *   lohnanteilBestaetigen({ netto, beschreibung, pruefenNamen })   → Promise<boolean>   (true = „Ja, so übernehmen“)
 *   belegLohnPruefen(art, id, onAendern)                           → Promise<boolean>   für gespeicherte Belege der Listen
 */
const eur = (x) => (Number(x) || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const LohnFrageFenster = ({ info, onAntwort }) => (
  <div className="fixed inset-0 z-[10000] bg-black/50 flex items-center justify-center p-4" data-testid="lohn-frage">
    <div className="bg-card text-foreground rounded-lg shadow-2xl w-full max-w-md p-5 space-y-3 border" role="dialog" aria-modal="true">
      <h3 className="text-base font-semibold">{info.leer ? "Kein Lohnanteil ermittelt" : "Lohnanteil übernehmen?"}</h3>
      <p className="text-sm" data-testid="lohn-frage-text">
        {info.leer
          ? <>Kein Lohnanteil ermittelt – {info.pruefenNamen?.length} {info.pruefenNamen?.length === 1 ? "Position" : "Positionen"} ohne Lohn. Der Lohnanteil wird dann nicht ausgewiesen.</>
          : <>Lohnanteil <b>{eur(info.netto)} € netto</b>{info.beschreibung ? ` (${info.beschreibung})` : ""} – so übernehmen?</>}
      </p>
      {info.pruefenNamen?.length > 0 && !info.leer && (
        <p className="text-xs rounded-sm border border-amber-300 bg-amber-50 text-amber-900 px-2 py-1.5" data-testid="lohn-frage-pruefen">
          {info.pruefenNamen.length} {info.pruefenNamen.length === 1 ? "Position hat" : "Positionen haben"} keinen Lohn („Lohn bitte prüfen“): {info.pruefenNamen.join(", ")}.
        </p>
      )}
      <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-1">
        <button type="button" onClick={() => onAntwort(false)} className="h-10 px-4 rounded-sm border text-sm font-medium" data-testid="lohn-frage-aendern">Ändern</button>
        <button type="button" onClick={() => onAntwort(true)} className="h-10 px-4 rounded-sm bg-primary text-primary-foreground text-sm font-medium" data-testid="lohn-frage-ja" autoFocus>{info.leer ? "Ohne Lohnanteil weiter" : "Ja, so übernehmen"}</button>
      </div>
    </div>
  </div>
);

export const lohnanteilBestaetigen = (info) => new Promise((resolve) => {
  const host = document.createElement("div");
  document.body.appendChild(host);
  const root = createRoot(host);
  const fertig = (ja) => { root.unmount(); host.remove(); resolve(ja); };
  root.render(<LohnFrageFenster info={info} onAntwort={fertig} />);
});

/** Eine Funktion für alle Wege: Betrag > 0 → „so übernehmen?“; Betrag 0 mit Positionen „Lohn bitte prüfen“ → „Kein Lohnanteil ermittelt“; sonst keine Frage. */
export const lohnanteilFragen = async (netto, beschreibung, zeilen) => {
  const namen = pruefenNamen(zeilen);
  if (netto > 0) return lohnanteilBestaetigen({ netto, beschreibung, pruefenNamen: namen });
  if (namen.length > 0) return lohnanteilBestaetigen({ netto: 0, leer: true, pruefenNamen: namen });
  return true;
};

/** Kurznamen der Positionen ohne Lohn (für den Hinweis in der Rückfrage). */
export const pruefenNamen = (zeilen) => zeilen.filter((z) => z.pruefen).map((z) => (z.pos.description || "").split(" - ")[0].substring(0, 30));

const PFAD = { quote: "quotes", order: "orders", invoice: "invoices" };

/**
 * Gespeicherten Beleg (Liste/Vorschau) vor PDF/Druck/.eml prüfen. Ohne Lohnanteil auf dem Beleg: true, keine Frage.
 * „Ändern“ → onAendern() (z. B. Beleg im Editor öffnen), Ergebnis false.
 */
export const belegLohnPruefen = async (art, id, onAendern) => {
  let doc;
  try { doc = (await api.get(`/${PFAD[art]}/${id}`)).data; } catch { return true; }   // Beleg nicht lesbar: nicht im Weg stehen
  if (!doc?.show_lohnanteil) return true;
  const positionen = (doc.positions || []).map((p) => ({ ...p, type: p.type || "position" }));
  let artikel = [];
  if (positionen.some((p) => !p.artikel_typ && p.source_article_id)) {
    try { artikel = (await api.get("/modules/artikel/data")).data || []; } catch { artikel = []; }
  }
  const sub = positionen.filter((p) => p.type !== "titel").reduce((s, p) => s + (Number(p.quantity) || 0) * (Number(p.price_net) || 0), 0);
  const nach = sub - ((doc.discount_type || "percent") === "percent" ? sub * ((Number(doc.discount) || 0) / 100) : (Number(doc.discount) || 0));
  const zeilen = lohnZeilen(positionen, artikel, rabattFaktor(sub, nach));
  const hand = doc.lohnanteil_custom === null || doc.lohnanteil_custom === undefined || doc.lohnanteil_custom === "" ? null : Number(String(doc.lohnanteil_custom).replace(",", "."));
  let netto; let beschreibung;
  if (hand !== null && Number.isFinite(hand)) {
    netto = hand;
    beschreibung = Math.abs(hand - lohnSumme(zeilen)) > 0.004 ? "von Hand gesetzt" : lohnBeschreibung(zeilen);
  } else {
    netto = Math.max(0, nach) * 0.6;    // gleiche Standardregel wie im PDF, solange kein Betrag gespeichert ist
    beschreibung = "60 % vom Netto, weil noch kein Lohn eingetragen ist";
  }
  const ja = await lohnanteilFragen(netto, beschreibung, hand !== null ? zeilen : []);
  if (!ja) onAendern?.();
  return ja;
};
